import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { API_ENDPOINT } from 'app/services/utils.service';
import { InviteUserResponse, User } from 'app/models/api/users';
import { ApiQuotaResponse } from 'app/models/api/api-response';
import { Order, OrderForRequestBody, PaginationForRequestBody } from '../models/order';
import { PaginationOptions } from '../models/pagination-options';
import { MetaDataReducerUtils } from '../utils/reducers/meta-data.utils';
import { UsersFilter } from '../models/reducers/users';
import { ArrayUtils } from '@festo-ax/smartenance-shared-ui';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private http: HttpClient = inject(HttpClient);

  private usersEndpoint: string = `${API_ENDPOINT}/users`;

  getUsers(order: Order, paginationOptions: PaginationOptions, filters: UsersFilter): Observable<ApiQuotaResponse<User>> {
    const url: string = this.usersEndpoint;
    const { offset, count }: PaginationForRequestBody = MetaDataReducerUtils.convertPaginationOptionsToRequest(paginationOptions);
    const { orderBy, orderDirection }: OrderForRequestBody = MetaDataReducerUtils.convertOrderToRequest(order);
    const { searchTerm }: UsersFilter = filters;

    let params: HttpParams = new HttpParams();
    params = params.append('searchTerm', searchTerm);
    params = params.append('count', count);
    params = params.append('offset', offset);
    params = params.append('orderBy', orderBy);
    params = params.append('orderDirection', orderDirection);

    return this.http.get<ApiQuotaResponse<User>>(url, { params });
  }

  inviteUser(user: { mailAddress: string }): Observable<InviteUserResponse> {
    return this.http.post<InviteUserResponse>(`${this.usersEndpoint}/invite`, user).pipe(
      mergeMap((response: InviteUserResponse) =>
        ArrayUtils.isNotEmpty(response.errorCodes)
          ? throwError(() => response)
          : of(response)
      ),
    );
  }

  deleteUsers(userIds: string[]): Observable<unknown> {
    return this.http.post(`${this.usersEndpoint}/delete`, [...userIds]);
  }

  getInvitations(): Observable<string[]> {
    return this.http.get<{ mailAddress: string }[]>(`${this.usersEndpoint}/invitations`).pipe(
      map(raw => raw.map(r => r.mailAddress)),
    );
  }

  deleteInvitation(mailAddress: string): Observable<{ mailAddress: string }> {
    return this.http.post<{ mailAddress: string }>(`${this.usersEndpoint}/cancel-invitation`, { mailAddress });
  }
}
